import { defineStore } from "pinia";
import { computed, ref } from "vue";

import { sendWsReconnect } from "@/composables/useEventBus";
import { RingBuffer } from "@/utils/RingBuffer";
import { asReadonly } from "@/utils/vue";

interface WebsocketState {
  timestamp: number;
  state: string;
}

export const useConnectionStore = defineStore("connection", () => {
  const netWs = ref(true);
  const netOnline = ref(true);
  const alm = ref(true);
  const reconnects = ref(0);
  const wsStates = new RingBuffer<WebsocketState>(20);
  const lastFrozen = ref(0);
  const lastUnfrozen = ref(0);

  return {
    netOnline: asReadonly(netOnline),
    reconnects: asReadonly(reconnects),
    lastFrozen: asReadonly(lastFrozen),
    lastUnfrozen: asReadonly(lastUnfrozen),
    alm: asReadonly(alm),

    isConnected: computed(() => netWs.value && netOnline.value),
    timeSinceLastUnfreeze: computed(() =>
      lastUnfrozen.value > 0 ? Date.now() - lastUnfrozen.value : undefined,
    ),

    freeze: () => (lastFrozen.value = Date.now()),
    unfreeze: () => (lastUnfrozen.value = Date.now()),
    setOffline: () => (netOnline.value = false),
    setOnline: () => (netOnline.value = true),
    setAlm: (running: boolean) => (alm.value = running),
    wsStates: () =>
      wsStates.get().map((entry) => `${new Date(entry.timestamp).toISOString()}: ${entry.state}`),
    disconnectWebsocket: () => (netWs.value = false),
    connectWebsocket: () => (netWs.value = true),
    reconnectWebsocket: () => {
      reconnects.value++;
      netWs.value = true;
      // Notify the board that the websocket has reconnected
      // We can't just watch for changes to netWs, because the disconnect event
      // isn't always processed (eg. when the computer goes to sleep)
      sendWsReconnect();
    },
    addWebsocketState: (state: string) => wsStates.add({ timestamp: Date.now(), state }),
  };
});
